import * as io from 'io-ts';

const VirtualCardIssuanceScopeType = io.keyof({ APPLICATION: null });

/** A new issuance request for a virtual card.
 * Endpoint: POST /api/virtual-card/issuance
 **/
export const VirtualCardIssuanceRequest = io.type({
  /** Only supports APPLICATION */
  scopeType: VirtualCardIssuanceScopeType,
  /** Only supports applicationID */
  scopeID: io.string,
  /** ID of the program associated with this issuance */
  programID: io.string,
  /** ID of the buyer associated with this issuance */
  buyerID: io.string,
});

export type VirtualCardIssuanceRequest = io.TypeOf<
  typeof VirtualCardIssuanceRequest
>;

const VirtualCardInStoreIssuance = io.type({
  applicationID: io.string,
  merchantID: io.string,
  merchantLocationID: io.string,
  offerID: io.string,
});

export enum VirtualCardIssuanceStatus {
  /** Issuance has been requested */
  REQUESTED = 'REQUESTED',
  /** Issuance is being created */
  PROCESSING = 'PROCESSING',
  /** Issuance has been provisioned */
  ISSUED = 'ISSUED',
  /** Issuance failed to provision */
  FAILED = 'FAILED',
  /** Issuance has gone through checkout and can no longer be modified */
  CLOSED = 'CLOSED',
}

const VirtualCardIssuance = io.intersection([
  io.type({
    id: io.string,
    /** Provisioning status of the issuance */
    status: io.keyof(VirtualCardIssuanceStatus),
    scopeType: io.string,
    scopeID: io.string,
  }),
  io.partial({
    inStore: VirtualCardInStoreIssuance,
    buyerID: io.string,
    programID: io.string,
    tenantID: io.string,
    paymentProductID: io.string,
  }),
]);

const VirtualCardCapacity = io.type({
  currency: io.string,
  value: io.number,
});

export const VirtualCardGPayIssuanceRequest = io.type({
  /** A string that identifies the backend session. Provided by Google pay backend. */
  serverSessionId: io.string,
  /** A string that identifies the client session. Provided by Google pay backend. */
  clientSessionId: io.string,
  /** 1 => Tokenization will be attempted.; 0 => Tokenization will not be attempted. */
  tokenSetting: io.number,
  /** 1 => FPAN save will be attempted.; 0 => FPAN save will not be attempted. */
  cardSetting: io.number,
  /** String identifying the device-scoped Google Pay wallet that will receive the token.
   * Provided by Google Pay backend. */
  publicWalletId: io.string,
  /** String identifying the Android device that will receive the token.
   * Provided by Google Pay backend */
  publicDeviceId: io.string,
  /** A Google-assigned string that uniquely identifies both the integrator that is
   * initiating the session and the issuer of the payment card. */
  integratorId: io.string,
});

export type VirtualCardGPayIssuanceRequest = io.TypeOf<
  typeof VirtualCardGPayIssuanceRequest
>;

export const VirtualCardGPayIssuanceResponse = io.intersection([
  io.type({
    /** ID for the wallet issuance request */
    walletID: io.string,
  }),
  io.partial({ state: io.string }),
]);

export type VirtualCardGPayIssuanceResponse = io.TypeOf<
  typeof VirtualCardGPayIssuanceResponse
>;

export enum VirtualCardStatus {
  /** Card not yet requested */
  NOT_CREATED = 'NOT_CREATED',
  REQUESTED = 'REQUESTED',
  /** Card in process of provisioning */
  PROVISIONING = 'PROVISIONING',
  /** Card is successfully provisioned */
  PROVISIONED = 'PROVISIONED',
  /** Card is ready to be used */
  ACTIVE = 'ACTIVE',
  /** Card failed provisioning */
  FAILED = 'FAILED',
}

export const VirtualCard = io.intersection([
  io.type({
    cardID: io.string,
    /** Provisioning status of the virtual card */
    status: io.keyof(VirtualCardStatus),
  }),
  io.partial({
    capacity: VirtualCardCapacity,
    lastFour: io.string,
    issuanceDate: io.string,
    /** Expiration date of the virtual card */
    expiry: io.string,
    pan: io.string,
    cvv: io.string,
  }),
]);

export type VirtualCard = io.TypeOf<typeof VirtualCard>;

export enum WalletType {
  APPLEPAY = 'APPLEPAY',
  GOOGLEPAY = 'GOOGLEPAY',
}

/** Provisioning status of a digital wallet token */
export enum WalletStatus {
  /** represents a digital wallet token in the process of provisioning */
  PROVISIONING = 'PROVISIONING',
  /** represents a successfully provisioned digital wallet token */
  ACTIVE = 'ACTIVE',
  /** represents a digitial wallet token that failed provisioning */
  FAILED = 'FAILED',
  /** represents a suspended digital wallet token. Can be reinstated. */
  SUSPENDED = 'SUSPENDED',
  /** represents a terminated digital wallet token. Cannot be reinstated. */
  TERMINATED = 'TERMINATED',
}

const ApplePay = io.type({
  state: io.string,
});

const GooglePay = io.type({
  publicWalletId: io.string,
  publicDeviceId: io.string,
});

const VirtualCardWallet = io.intersection([
  io.type({
    id: io.string,
    type: io.keyof(WalletType),
    /** Provisioning status of a digital wallet token */
    status: io.keyof(WalletStatus),
  }),
  io.partial({
    /** Wallet DPAN. Only available with r.vc.restricted scope. */
    pan: io.string,
    applePay: ApplePay,
    googlePay: GooglePay,
    /** indicates if this wallet is unrecoverable because of red path */
    error: io.boolean,
  }),
]);

export type VirtualCardWallet = io.TypeOf<typeof VirtualCardWallet>;

const VirtualCardIssuanceSummary = io.type({
  /** Total authorized transaction */
  authorized: io.number,
  /** Total settled transaction */
  settled: io.number,
  /** Total refunded transaction */
  refunded: io.number,
});

export const VirtualCardIssuanceResponse = io.intersection([
  io.type({
    issuance: VirtualCardIssuance,
  }),
  io.partial({
    card: VirtualCard,
    /** Wallets associated with the virtual card */
    wallets: io.array(VirtualCardWallet),
    /** Total authorized/settled/refunded transactions */
    summary: VirtualCardIssuanceSummary,
  }),
]);

export type VirtualCardIssuanceResponse = io.TypeOf<
  typeof VirtualCardIssuanceResponse
>;

export const VirtualCardIssuanceDisplayTokenResponse = io.type({
  displayToken: io.string,
});

export type VirtualCardIssuanceDisplayTokenResponse = io.TypeOf<
  typeof VirtualCardIssuanceDisplayTokenResponse
>;

export const ApplePayJWSHeader = io.type({ kid: io.string });

export const ApplePayJWS = io.type({
  header: ApplePayJWSHeader,
  protected: io.string,
  payload: io.string,
  signature: io.string,
});

export const AppleWalletIssuanceRequest = io.type({});

export const AppleWalletIssuanceResponse = io.intersection([
  io.type({ walletID: io.string }),
  io.partial({ state: io.string, jws: ApplePayJWS }),
]);

export type AppleWalletIssuanceRequest = io.TypeOf<
  typeof AppleWalletIssuanceRequest
>;

export type AppleWalletIssuanceResponse = io.TypeOf<
  typeof AppleWalletIssuanceResponse
>;

export type ResultResolverArg = {
  status: string;
  statusMessage: string;
  state: string;
};
